import {
  Box,
  BoxProps,
  Flex,
  Heading,
  Icon,
  SelectInput,
  smBumps,
} from '@wrisk/ui-components'
import { isMatch, isNil } from 'lodash-es'
import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react'

import { Quote } from '../../../../../domain'
import { useCoverLevelExcessConfig, useExcesses } from '../../../../../hooks/excess'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { logger } from '../../../../../infrastructure/logging'
import {
  ExcessCombination,
  getHelpContentBaseUrl,
  useConfig,
} from '../../../../../state/configuration'
import { HelpFlexAnchor } from '../../../links'
import { generateSlug } from './generateSlug'
import { textGenerator } from './textGenerator'

interface ExcessSelectionProps extends BoxProps {
  tKey: TKeyBuilder
  quote: Quote
  onChange?: (excess: ExcessCombination) => void
  loading?: boolean
}

const key = 'excess-selection'

export const ExcessSelection: FunctionComponent<ExcessSelectionProps> = ({
  tKey,
  quote,
  onChange,
  loading,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const { combinations } = useExcesses(quote)

  const excessConfig = useCoverLevelExcessConfig()

  const contentBaseUrl = useConfig(getHelpContentBaseUrl)

  const values = useMemo(() => {
    const generateText = textGenerator(t)

    return (
      combinations?.map((combination) => ({
        value: generateSlug(combination),
        text: generateText(combination),
        combination,
      })) ?? []
    )
  }, [combinations])

  const handleChange = useCallback(
    (slug: string) => {
      const value = values.find((it) => it.value === slug)

      if (value) {
        onChange?.(value.combination)
      } else {
        logger.error(`Unknown combination: ${slug}`)
      }
    },
    [values],
  )

  const header = t(tKey(key, 'header'), { defaultValue: null })
  const value = values.find((it) => isMatch(quote.voluntaryExcesses, it.combination))

  const shouldRenderComponent = excessConfig && values.length > 0

  useEffect(() => {
    if (isNil(value) && values.length) {
      logger.warn(`Invalid excess combination found`)
      onChange?.(values[0].combination)
    }
  }, [value, values])

  return shouldRenderComponent ? (
    <Box display='flex' flexDirection='column' gap={smBumps} width={1} {...props}>
      {header && (
        <Flex width={1}>
          <Heading variant='h4' as='h4' typoSize='md'>
            {header}
          </Heading>
          {excessConfig?.helpLink && (
            <HelpFlexAnchor
              variant='highlight'
              borderRadius='50%'
              justifyContent='center'
              ml={smBumps}
              p={1}
              href={`${contentBaseUrl}/${key}.html`}
            >
              <Icon icon='help' size='medium' />
            </HelpFlexAnchor>
          )}
        </Flex>
      )}

      <SelectInput<string>
        value={value?.value}
        values={values}
        onChange={handleChange}
        loading={loading}
      />
    </Box>
  ) : null
}
